import type Guid from "@/app/types/common/guid";
import { tenantService } from "@/app/services/extensions/tenantServiceExtended";
import type BaseEnumType from "@/app/services/baseEnumType";
import { EnumDiscountBasis } from "@/app/services/generated/enums/enumDiscountBasis.generated";
import { EnumDiscountTierBasis } from "@/app/services/generated/enums/enumDiscountTierBasis.generated";
import { EnumBogofQuantityBasis } from "@/app/services/generated/enums/enumBogofQuantityBasis.generated";
import type { Maybe } from "@/components/utils/typeHelpers";

export class EnumLocaleAwareField implements BaseEnumType {
  private static allValues: EnumLocaleAwareField[] = []; // make sure this is top-most

  public static none = new EnumLocaleAwareField(
    0,
    "None",
    "enum_locale_aware_field_none"
  );
  public static australianBusinessNumber = new EnumLocaleAwareField(
    1,
    "AustralianBusinessNumber",
    "enum_locale_aware_field_australian_business_number"
  );
  public static discountBasis = new EnumLocaleAwareField(
    2,
    "DiscountBasis",
    "enum_locale_aware_field_discount_basis"
  );
  public static discountTierBasis = new EnumLocaleAwareField(
    3,
    "DiscountTierBasis",
    "enum_locale_aware_field_discount_tier_basis"
  );
  public static bogofQuantityBasis = new EnumLocaleAwareField(
    4,
    "BogofQuantityBasis",
    "enum_locale_aware_field_bogof_quantity_basis"
  );
  public static employerIdentificationNumber = new EnumLocaleAwareField(
    5,
    "EmployerIdentificationNumber",
    "enum_locale_aware_field_employer_identification_number"
  );

  public static ttbBrewersNotice = new EnumLocaleAwareField(
    6,
    "TtbBrewersNotice",
    "enum_locale_aware_field_ttb_brewers_notice"
  );

  public key: number;
  public value: string;
  public translationKey?: string;

  private constructor(key: number, value: string, translationKey?: string) {
    this.key = key;
    this.value = value;
    this.translationKey = translationKey;
    EnumLocaleAwareField.allValues.push(this);
  }

  public equals(other: Maybe<EnumLocaleAwareField>): boolean {
    return this.key === other?.key;
  }

  public isNone() {
    return this.equals(EnumLocaleAwareField.none);
  }
  public isAustralianBusinessNumber() {
    return this.equals(EnumLocaleAwareField.australianBusinessNumber);
  }
  public isDiscountBasis() {
    return this.equals(EnumLocaleAwareField.discountBasis);
  }
  public isDiscountTierBasis() {
    return this.equals(EnumLocaleAwareField.discountTierBasis);
  }
  public isBogofQuantityBasis() {
    return this.equals(EnumLocaleAwareField.bogofQuantityBasis);
  }
  public isEmployerIdentificationNumber() {
    return this.equals(EnumLocaleAwareField.employerIdentificationNumber);
  }
  public isTtbBrewersNotice() {
    return this.equals(EnumLocaleAwareField.ttbBrewersNotice);
  }

  public static getValues(): EnumLocaleAwareField[] {
    return EnumLocaleAwareField.allValues;
  }

  public static getByKey(key: number): EnumLocaleAwareField {
    const found = EnumLocaleAwareField.allValues.find((e) => e.key === key);
    if (found === undefined) {
      throw new Error(`EnumLocaleAwareField - Key not found: ${key}`);
    }

    return found;
  }
}

export interface LocaleAwareFieldRule {
  field: EnumLocaleAwareField;
  isApplicable: boolean;

  /**
   * If this field is an enum - a list on values keys that are not applicable
   */
  enumValuesNotApplicable?: number[];
}

/**
 * This service is not extended from a server client library as it's a POC. If proven to be the right solution then
 * it will be migrated.
 */
export default class LocalisationServiceExtended {
  /**
   * Gets the rules for all then locale aware fields.
   */
  async getAllLocaleAwareFieldRules(
    tenantId: Guid
  ): Promise<LocaleAwareFieldRule[]> {
    const tenant = await tenantService.getTenant(tenantId);

    return [
      {
        field: EnumLocaleAwareField.australianBusinessNumber,
        isApplicable: tenant.locationConfiguration.locationCountryCode === "AU",
      },
      {
        field: EnumLocaleAwareField.discountBasis,
        isApplicable: true,
        enumValuesNotApplicable:
          tenant.locationConfiguration.locationCountryCode !== "GB"
            ? [EnumDiscountBasis.currencyPerBarrel.key]
            : [],
      },
      {
        field: EnumLocaleAwareField.discountTierBasis,
        isApplicable: true,
        enumValuesNotApplicable:
          tenant.locationConfiguration.locationCountryCode !== "GB"
            ? [
                EnumDiscountTierBasis.byLineAs9Gallons.key,
                EnumDiscountTierBasis.byPricingCategoryAs9Gallons.key,
              ]
            : [],
      },
      {
        field: EnumLocaleAwareField.bogofQuantityBasis,
        isApplicable: true,
        enumValuesNotApplicable:
          tenant.locationConfiguration.locationCountryCode !== "GB"
            ? [EnumBogofQuantityBasis.volumeAs9Gs.key]
            : [],
      },
      {
        field: EnumLocaleAwareField.employerIdentificationNumber,
        isApplicable: tenant.locationConfiguration.locationCountryCode === "US",
      },
      {
        field: EnumLocaleAwareField.ttbBrewersNotice,
        isApplicable: tenant.locationConfiguration.locationCountryCode === "US",
      },
    ];
  }
}

export const localisationService = new LocalisationServiceExtended();
